import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import { getErrorMessage } from "../../common";
import { horarioBonito } from "../../utils/horarioBonito";
import { horasTexto } from "./rotationPicker";
import { BLOCK_KIND, BLOCK_STATUS, BlockDTO, EnrollmentBlockDTO, EnrollmentBlocksDTO } from "../../models/CourseBlockDTO";
import { BlockKindPill, seTomaEn } from "../../navigation/management/courseBlocksPrompt";

export interface EnrollmentBlocksCardProps {
	data: EnrollmentBlocksDTO;
	/** Resolves with the refreshed payload; rejects with the axios error. */
	onAssign: (blockId: number, scheduleId: number | null) => Promise<EnrollmentBlocksDTO>;
	onChanged: (data: EnrollmentBlocksDTO) => void;
	/** Called after every successful PUT: moving to a block changes the enrollment's schedule, which the parent card header shows. */
	onReload?: () => void;
	canEdit: boolean;
	idPrefix?: string;
	/** Inglés después del diplomado: abre su propia matrícula con los meses que el diplomado no incluye (pagados). */
	onContinueParallel?: () => Promise<EnrollmentBlocksDTO>;
	/** Pasar a otro bloque mueve la matrícula: se hace en Actualizaciones › Siguiente Bloque (2026-10-26). */
	linkSiguienteBloque?: string;
	/** Si viene, abre Actualizaciones encima de la ficha en vez de navegar (un solo camino, 2026-10-29). */
	onSiguienteBloque?: () => void;
}

const pill: React.CSSProperties = {
	display: "inline-block", padding: "2px 8px", fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
	color: "#6B5F58", background: "#FAF7F4", border: "1px solid #E9E1DA", borderRadius: 999, whiteSpace: "nowrap"
};

const pillCursando: React.CSSProperties = { ...pill, color: "#FFFFFF", background: "#F45030", borderColor: "#F45030" };

const colorEstatus = (statusId: number): string => {
	if (statusId === BLOCK_STATUS.COMPLETE) return "#1F7A54";
	if (statusId === BLOCK_STATUS.IN_PROGRESS) return "#F45030";
	return "#6B5F58";
};

const porcentaje = (taken: number, target: number): number => {
	if (!(target > 0)) return 0;
	return Math.max(0, Math.min(100, Math.round((Number(taken || 0) / target) * 100)));
};

/**
 * "En paralelo" (Inglés) and "Opcional" blocks are an ADDITIONAL authorized schedule: assigning one never moves the
 * enrollment, so it can also be removed. Every other block is where the enrollment lives while it is taken.
 */
export const esBloqueAdicional = (block: BlockDTO): boolean =>
	block.kindId === BLOCK_KIND.PARALLEL || block.kindId === BLOCK_KIND.OPTIONAL;

/** Thin progress bar: orange while in progress, green when complete. */
const Barra: React.FC<{ taken: number; target: number }> = ({ taken, target }) => {
	const pct = porcentaje(taken, target);
	return (
		<div style={{ height: 4, background: "#E9E1DA", borderRadius: 999, overflow: "hidden", marginTop: 4 }}>
			<div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#1F7A54" : "#F45030" }} />
		</div>
	);
};

/**
 * Bloques de una matrícula en curso compuesto (Esteban, 2026-09-18): one row per block with the schedule where
 * the student takes it, the hours done and the status. The enrollment lives in the schedule of the block being
 * taken right now ("Cursando ahora"); "Pasar a este bloque" moves it to a schedule of another block without
 * re-billing, while "En paralelo" blocks only get an extra authorized schedule (same mechanism as rotativos).
 */
const EnrollmentBlocksCard: React.FC<EnrollmentBlocksCardProps> = ({ data, onAssign, onChanged, onReload, canEdit, idPrefix, onContinueParallel, linkSiguienteBloque, onSiguienteBloque }) => {
	const prefix = idPrefix || "blocks";
	const [editingBlockId, setEditingBlockId] = useState<number | null>(null);
	const [draftScheduleId, setDraftScheduleId] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const startEdit = (row: EnrollmentBlockDTO) => {
		setError(null);
		setDraftScheduleId(row.scheduleId == null ? "" : String(row.scheduleId));
		setEditingBlockId(row.block.id);
	};

	const assign = async (row: EnrollmentBlockDTO, scheduleId: number | null) => {
		setBusy(true);
		setError(null);
		try {
			const refreshed = await onAssign(row.block.id, scheduleId);
			onChanged(refreshed);
			setEditingBlockId(null);
			if (onReload) {
				onReload();
			}
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const continuar = async (row: EnrollmentBlockDTO) => {
		if (!onContinueParallel || !window.confirm(`¿Abrir la matrícula de ${row.block.name} con los meses que el diplomado no incluye? Esos meses se cobran al precio normal.`)) return;
		setBusy(true);
		setError(null);
		try {
			onChanged(await onContinueParallel());
			if (onReload) onReload();
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const linea: React.CSSProperties = { fontSize: 13, color: "#6B5F58" };

	/** What the button next to the schedule says for a block that is not being edited. */
	const accion = (row: EnrollmentBlockDTO): { label: React.ReactNode; title: string } | null => {
		if (!canEdit || row.isCurrent) {
			return null;
		}
		if (esBloqueAdicional(row.block)) {
			return row.scheduleId == null
				? { label: Constants.Common.assignSchedule, title: Constants.Common.assignSchedule }
				: { label: <FontAwesomeIcon icon={solid("pen")} />, title: Constants.Common.edit };
		}
		return { label: Constants.Common.moveToBlock, title: Constants.Common.moveToBlock };
	};

	return (
		<div style={{ background: "#FAF7F4", border: "1px solid #E9E1DA", borderRadius: 6, padding: "10px 14px", marginTop: 8 }}>
			<div style={{ fontWeight: 600, marginBottom: 6 }}>{Constants.Common.blocks}</div>
			{error ? <div style={{ fontSize: 13, color: "#A8730A", marginBottom: 6 }}>{error}</div> : ""}
			<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
				{data.blocks.map((row) => {
					const editing = editingBlockId === row.block.id;
					const adicional = esBloqueAdicional(row.block);
					const boton = editing ? null : accion(row);
					return (
						<div key={`${prefix}_${row.block.id}`} style={{
							background: "#FFFFFF", border: `1px solid ${row.isCurrent ? "#F45030" : "#E9E1DA"}`, borderRadius: 6, padding: "8px 10px"
						}}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
								<div style={{ flex: 1, minWidth: 200 }}>
									<b>{row.block.name}</b>&nbsp;<BlockKindPill kindId={row.block.kindId} label={row.block.kindLabel} />
									{row.isCurrent ? <>&nbsp;<span style={pillCursando}>{Constants.Common.takingNow}</span></> : ""}
									<div style={linea}>{Constants.Common.blockTakenIn}: {seTomaEn(row.block)}</div>
									<div style={linea}>
										{Constants.Common.schedule}: {row.scheduleName ? horarioBonito(row.scheduleName) : Constants.Common.noScheduleAssigned}
										{boton && adicional ? (
											<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px", fontSize: 12 }} disabled={busy}
												title={boton.title} onClick={() => startEdit(row)}>
												{boton.label}
											</button>
										) : boton && onSiguienteBloque && !row.isCurrent ? (
											<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px", fontSize: 12 }} onClick={onSiguienteBloque}>
												{Constants.Common.moveToBlock}
											</button>
										) : boton && linkSiguienteBloque && !row.isCurrent ? (
											<Link className="btn btn-link btn-sm" style={{ padding: "0 6px", fontSize: 12 }} to={linkSiguienteBloque}>
												{Constants.Common.moveToBlock}
											</Link>
										) : ""}
									</div>
								</div>
								<div style={{ minWidth: 150, flexShrink: 0 }}>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 13 }}>
										<span>{horasTexto(row.hoursTaken)} / {horasTexto(row.hoursTarget)}</span>
										<span style={{ ...pill, color: colorEstatus(row.statusId), borderColor: colorEstatus(row.statusId) }}>{row.statusLabel}</span>
									</div>
									<Barra taken={row.hoursTaken} target={row.hoursTarget} />
									{canEdit && onContinueParallel && row.block.kindId === BLOCK_KIND.PARALLEL && row.scheduleId != null && row.statusId !== BLOCK_STATUS.COMPLETE ? (
										<button type="button" className="btn btn-link btn-sm" style={{ padding: "4px 0 0", fontSize: 12 }} disabled={busy} onClick={() => continuar(row)}>
											Continuar {row.block.name} (meses pagados)
										</button>
									) : ""}
								</div>
							</div>
							{editing ? (
								<div style={{ marginTop: 8 }}>
									{row.options.length < 1 ? (
										<div style={linea}>{Constants.Common.noBlockSchedules}</div>
									) : (
										<select id={`${prefix}_s_${row.block.id}`} className="form-select form-select-sm" value={draftScheduleId} disabled={busy}
											onChange={(event) => setDraftScheduleId(event.target.value)}>
											<option value="">{adicional ? Constants.Common.noScheduleAssigned : Constants.Common.chooseNewSchedule}</option>
											{row.options.map((option) => (
												<option key={`${prefix}_o_${option.id}`} value={String(option.id)}>
													{[horarioBonito(option.name), option.mentorName, horasTexto(option.weeklyHours)].filter(Boolean).join(" · ")}
												</option>
											))}
										</select>
									)}
									{!adicional && row.options.length > 0 ? (
										<div style={{ ...linea, color: "#2B1C16", marginTop: 6 }}>{Constants.Common.moveToBlockHint}</div>
									) : ""}
									<div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6, flexWrap: "wrap" }}>
										{adicional && row.scheduleId != null ? (
											<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px", fontSize: 12, color: "#A8730A", marginRight: "auto" }}
												disabled={busy} onClick={() => assign(row, null)}>
												{Constants.Common.removeSchedule}
											</button>
										) : ""}
										<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={() => setEditingBlockId(null)}>
											{Constants.Common.close}
										</button>
										{adicional ? (
											<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || draftScheduleId === ""}
												onClick={() => assign(row, Number(draftScheduleId))}>
												{Constants.Common.save}
											</button>
										) : (
											<button type="button" className="btn btn-sm rounded-pill" style={{ background: "#F45030", borderColor: "#F45030", color: "#FFFFFF" }}
												disabled={busy || draftScheduleId === "" || Number(draftScheduleId) === row.scheduleId}
												onClick={() => assign(row, Number(draftScheduleId))}>
												{Constants.Common.moveToBlock}
											</button>
										)}
									</div>
								</div>
							) : ""}
						</div>
					);
				})}
			</div>
			<div style={{ fontSize: 13, marginTop: 8, textAlign: "right" }}>
				<span style={{ color: "#6B5F58" }}>{Constants.Common.total}:</span> <b>{horasTexto(data.totals?.hoursTaken)} / {horasTexto(data.totals?.hoursTarget)}</b>
			</div>
		</div>
	);
};

export default EnrollmentBlocksCard;
